<script>
  import { Modal } from "carbon-components-svelte";
  import { Tooltip, Button } from "paxray-components-svelte";
  import { tick } from "svelte";

  export let open;
  export let modalHeading;
  export let actions = false;
  export let subtitle = "";
  export let tooltipText = "";
  // Blendet den Tooltip-Slot aus, etwa wenn er nur in einem Modus des Modals passt
  export let showTooltipSlot = true;
  export let buttonSize = "field";
  export let width = 500;
  export let xs = false;
  export let autowidth = false;
  export let fitcontent = false;
  export let xxl = false;
  export let hasScrollingContent = true;
  export let modalClass = "";
  export let primaryButtonText = "Save";
  export let primaryButtonVariant = "primary";
  export let secondaryButtonText = "Cancel";
  export let primaryButtonValidations = false;
  export let primaryButtonNotAllow = false;
  // Shown on hover while primaryButtonValidations disables the primary button.
  export let primaryButtonDisabledTooltip = "";
  export let noSecondaryButton = false;
  // Fuer echte Alternativ-Aktionen statt Cancel, der Button bleibt dann rechts neben dem Hauptbutton
  export let secondaryButtonRight = false;
  export let hideFooter = false;
  export let primaryButtonAction = () => {};
  export let preventCloseOnClickOutside = true;
  export let secondaryButtonAction = () => {
    open = false;
  };

  let modalWrapper;

  $: if (open && modalWrapper) {
    focusModal();
  }

  async function focusModal() {
    await tick();
    setTimeout(() => {
      if (modalWrapper) {
        modalWrapper.focus();
      }
    }, 10);
  }
</script>

<Modal
  class="custom-modal {xxl ? 'xxl' : ''} {modalClass}"
  bind:open
  {modalHeading}
  on:close
  passiveModal
  {hasScrollingContent}
  size={xs ? "xs" : undefined}
  {preventCloseOnClickOutside}
>
  <svelte:fragment slot="heading">
    <h3
      id="bx--modal-header__heading--modal-ccs-0.svj7woysq9"
      class="bx--modal-header__heading"
      class:with-tooltip={Boolean(tooltipText) || ($$slots.tooltip && showTooltipSlot)}
    >
      {modalHeading}
      {#if $$slots.tooltip && showTooltipSlot}<Tooltip><slot name="tooltip" /></Tooltip>
      {:else if tooltipText}<Tooltip text={tooltipText} />{/if}
    </h3>
    {#if subtitle}
      <p class="modal-subtitle">{subtitle}</p>
    {/if}
  </svelte:fragment>
  <div
    class="modal_wrapper"
    style="width:{width}px"
    class:auto={autowidth === true}
    class:fitcontent={fitcontent === true}
    bind:this={modalWrapper}
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    on:keydown={(e) => {
      if (
        e.key === "Enter" &&
        !primaryButtonNotAllow &&
        !primaryButtonValidations
      ) {
        primaryButtonAction();
      }
    }}
  >
    <div class="divider" />
    <slot />
    {#if !hideFooter}
      <div class="divider divider-margin" />
      <div class="buttons-container">
        <div class="left-buttons-container">
          <!-- @Adrian irgendwas stimmt bei dem modal nicht, nutzer anlegen bugt massiv -->
          {#if actions && !noSecondaryButton && !secondaryButtonRight}
            <Button
              on:click={secondaryButtonAction}
              text={secondaryButtonText}
              kind="outline-dark"
              size={buttonSize}
              iconLeft={false}
            />
          {/if}
          <slot name="left-buttons" />
        </div>
        <div class="actions-buttons-container">
          {#if actions}
            {#if !noSecondaryButton && secondaryButtonRight}
              <Button
                on:click={secondaryButtonAction}
                text={secondaryButtonText}
                kind="outline-dark"
                size={buttonSize}
                iconLeft={false}
              />
            {/if}
            <slot name="action-buttons" />
            {#if primaryButtonValidations && primaryButtonDisabledTooltip}
              <Tooltip hideIcon direction="left">
                <span slot="triggerText" class="disabled-primary-button">
                  <Button
                    text={primaryButtonText}
                    variant={primaryButtonVariant}
                    size={buttonSize}
                    iconLeft={false}
                    disabled
                  />
                </span>
                <span class="disabled-primary-tooltip">{primaryButtonDisabledTooltip}</span>
              </Tooltip>
            {:else}
              <Button
                on:click={primaryButtonAction}
                text={primaryButtonText}
                variant={primaryButtonVariant}
                size={buttonSize}
                iconLeft={false}
                bind:disabled={primaryButtonValidations}
              />
            {/if}
          {/if}
        </div>
      </div>
    {/if}
  </div>
</Modal>

<style>
  .disabled-primary-button {
    display: inline-flex;
  }

  /* Disabled buttons swallow hover in some browsers, the tooltip trigger must receive it. */
  .disabled-primary-button :global(button:disabled) {
    pointer-events: none;
  }

  .disabled-primary-tooltip {
    white-space: pre-line;
  }

  .with-tooltip {
    display: flex;
    align-items: center;
    gap: var(--control-gap);
  }

  :global(.custom-modal .bx--modal-content) {
    /* overflow-y: initial !important; */
    margin-bottom: 0 !important;
    outline: none !important;
  }

  /* Zusammen mit dem Container-Padding ergibt das unter den Buttons
     denselben Abstand wie zwischen Trennlinie und Buttons. */
  :global(.bx--modal-scroll-content > *:last-child) {
    padding-bottom: 1rem !important;
  }

  .buttons-container {
    display: flex;
    justify-content: space-between;
    gap: 9px;
    margin-top: 1.5rem;
  }

  /* Layout-neutral, damit Slot-Inhalte wie bisher direkt im Footer-Flex liegen */
  .left-buttons-container {
    display: contents;
  }

  .actions-buttons-container {
    display: flex;
    justify-content: flex-end;
    gap: 9px;
    margin-left: auto;
  }

  .modal_wrapper:focus {
    outline: none;
  }

  /* The width prop is set inline. Clamp it so a modal can never be wider than
     the content box it sits in - otherwise a vertical scrollbar pushes the
     content out and adds a horizontal scrollbar on top. */
  .modal_wrapper {
    max-width: 100%;
    margin-inline: auto;
  }

  :global(.bx--modal-close) {
    border-radius: 0.5rem;
  }

  :global(.bx--modal-close:focus) {
    border: none;
  }

  :global(.bx--modal-container) {
    background-color: white !important;
    border-radius: 0.5rem;
    padding: 0.5rem;
    /* width: auto !important; */
    overflow: initial !important;
  }

  :global(.custom-modal.xxl .bx--modal-container) {
    width: 84% !important;
  }

  :global(.custom-modal.filter-modal .bx--modal-content) {
    max-height: none !important;
    overflow-y: visible !important;
  }

  :global(.custom-modal.filter-modal .bx--modal-container) {
    overflow: visible !important;
  }

  :global(.bx--modal-header__heading) {
    color: var(--pax-black) !important;
    font-family: Inter !important;
    font-size: 14px !important;
    font-style: normal !important;
    font-weight: 700 !important;
    line-height: normal !important;
  }

  /* Smaller and grey, so heading and subtitle no longer look the same size. */
  .modal-subtitle {
    color: var(--pax-grey);
    font-size: 12px;
    line-height: 1.4;
  }

  .divider {
    width: 100%;
    height: 1px;
    background: #d9d9d9;
    margin-bottom: 1rem;
  }

  .divider-margin {
    margin-top: 1rem;
  }

  .auto {
    width: 100% !important;
  }

  .fitcontent {
    width: fit-content !important;
  }
</style>
